import { apiFetch, ApiNetworkError } from './apiClient';

/** ADR 0016: the artifact kinds that carry a discussion (phase 1). */
export type DiscussionKind = 'molecule-drawing' | 'molecule';

/** ADR 0016 D2: what part of the artifact a comment is about; absent for the whole artifact. */
export type CommentAnchor =
  | { readonly kind: 'atom'; readonly atomId: string }
  | { readonly kind: 'atom-set'; readonly atomIds: readonly string[] }
  | { readonly kind: 'bond'; readonly bondId: string }
  | { readonly kind: 'bond-set'; readonly bondIds: readonly string[] }
  | { readonly kind: 'property'; readonly property: string }
  /**
   * #910 (ADR 0017 D11): a factsheet statement -- a key derived from its source and text, a short
   * quote and its source, so the comment reads on its own.
   */
  | { readonly kind: 'statement'; readonly statementKey: string; readonly quote: string; readonly source: string }
  /** #920 (ADR 0018 D16): a property value (an assertion), with a short label so the comment reads on its own. */
  | { readonly kind: 'assertion'; readonly assertionId: string; readonly label: string };

/**
 * ADR 0016 D19–D22 (#910): a cited reference. `standing` (D20): ACCEPTED (peer-reviewed, patents,
 * curated databases, books), PREPRINT, WEB or PERUSELAB; `integrity` (D22): OK, RETRACTED or
 * LINK_UNAVAILABLE.
 */
export interface Reference {
  readonly id: string;
  readonly kind: string;
  readonly system: string;
  readonly identifier: string;
  readonly title: string | null;
  readonly authors: readonly string[];
  readonly venue: string | null;
  readonly year: number | null;
  readonly url: string | null;
  readonly standing: 'ACCEPTED' | 'PREPRINT' | 'WEB' | 'PERUSELAB';
  readonly integrity: 'OK' | 'RETRACTED' | 'LINK_UNAVAILABLE';
}

/** D21: what supports a comment -- an accepted source, PeruseLab data, or nothing (an opinion or a question). */
export type CommentFooting = 'SOURCED' | 'DATA' | null;

export interface DiscussionComment {
  readonly id: string;
  readonly parentId: string | null;
  /** The author's display name; null until one is recorded (never an email). */
  readonly authorName: string | null;
  /** Null for a deleted comment kept for its replies ("comment deleted"). */
  readonly body: string | null;
  readonly createdAt: string;
  readonly editedAt: string | null;
  readonly deleted: boolean;
  /** Only ever true for the artifact's moderator, who alone sees hidden comments. */
  readonly hidden: boolean;
  readonly mine: boolean;
  /** Only for the artifact's moderator. */
  readonly reportCount: number | null;
  readonly anchor?: CommentAnchor | null;
  /** The anchored atoms/bonds no longer exist: "about an earlier version" (D2). */
  readonly anchorOutdated: boolean;
  /** #910: numbered citations, in order. */
  readonly references: readonly Reference[];
  readonly footing: CommentFooting;
  /** How many readers flagged "citation needed", and whether the viewer did. */
  readonly citationNeeded: number;
  readonly citationNeededByMe: boolean;
  readonly replies: readonly DiscussionComment[];
}

export interface DiscussionThread {
  readonly comments: readonly DiscussionComment[];
  readonly canComment: boolean;
  readonly moderator: boolean;
  /** For the moderator: other people's live comments, for the D14 narrowing warning. */
  readonly byOthers: { readonly comments: number; readonly people: number } | null;
}

export interface MyComment {
  readonly id: string;
  readonly artifactKind: DiscussionKind;
  readonly artifactId: string;
  /** False once the commenter can no longer read the artifact (its title is then withheld). */
  readonly artifactAvailable: boolean;
  readonly artifactTitle: string | null;
  readonly reply: boolean;
  readonly body: string;
  readonly createdAt: string;
  readonly editedAt: string | null;
  readonly hidden: boolean;
}

/** Why a discussion request failed, as a stable kind the UI words for itself (as OCSR does, #878). */
export type DiscussionFailureKind =
  'notFound' | 'invalid' | 'rateLimited' | 'sessionExpired' | 'unavailable' | 'unknown';

export class DiscussionError extends Error {
  constructor(
    readonly kind: DiscussionFailureKind,
    message: string
  ) {
    super(message);
    this.name = 'DiscussionError';
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await apiFetch(path, init);
  } catch (error) {
    throw new DiscussionError('unavailable', error instanceof ApiNetworkError ? error.message : 'Network error.');
  }
  if (!response.ok) {
    const kind: DiscussionFailureKind =
      response.status === 404
        ? 'notFound'
        : response.status === 400
          ? 'invalid'
          : response.status === 429
            ? 'rateLimited'
            : response.status === 401
              ? 'sessionExpired'
              : response.status >= 500
                ? 'unavailable'
                : 'unknown';
    const problem = (await response.json().catch(() => undefined)) as { detail?: unknown } | undefined;
    throw new DiscussionError(kind, typeof problem?.detail === 'string' ? problem.detail : `Status ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const json = (body: unknown): RequestInit => ({
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body)
});

const threadPath = (kind: DiscussionKind, artifactId: string) =>
  `/discussions/${encodeURIComponent(kind)}/${encodeURIComponent(artifactId)}/comments`;

/** D24: the conversation's order (oldest first), or the comments citing the most sources first. */
export type DiscussionSort = 'oldest' | 'most-referenced';

export function getDiscussion(
  kind: DiscussionKind,
  artifactId: string,
  sort: DiscussionSort = 'oldest'
): Promise<DiscussionThread> {
  return call<DiscussionThread>(
    sort === 'most-referenced' ? `${threadPath(kind, artifactId)}?sort=most-referenced` : threadPath(kind, artifactId)
  );
}

export function postComment(
  kind: DiscussionKind,
  artifactId: string,
  comment: {
    readonly body: string;
    readonly parentId?: string;
    readonly anchor?: CommentAnchor;
    /** #910: stored reference ids, in citation order. */
    readonly references?: readonly string[];
  }
): Promise<DiscussionComment> {
  return call<DiscussionComment>(threadPath(kind, artifactId), { method: 'POST', ...json(comment) });
}

/** `references` omitted keeps the comment's references; a list (even empty) replaces them (#910). */
export function editComment(
  commentId: string,
  body: string,
  references?: readonly string[]
): Promise<DiscussionComment> {
  return call<DiscussionComment>(`/comments/${encodeURIComponent(commentId)}`, {
    method: 'PUT',
    ...json(references ? { body, references } : { body })
  });
}

/** #910: a pasted DOI, PMID, arXiv ID, CID, ChEBI ID, CAS RN, patent, ISBN or URL -> a stored reference. */
export function resolveReference(input: string): Promise<Reference> {
  return call<Reference>('/references/resolve', { method: 'POST', ...json({ input }) });
}

/** D21: flag (or withdraw a flag) "citation needed". */
export function setCitationNeeded(commentId: string, flagged: boolean): Promise<void> {
  return call<void>(`/comments/${encodeURIComponent(commentId)}/citation-needed`, {
    method: flagged ? 'POST' : 'DELETE'
  });
}

export function deleteComment(commentId: string): Promise<void> {
  return call<void>(`/comments/${encodeURIComponent(commentId)}`, { method: 'DELETE' });
}

export function setCommentHidden(commentId: string, hidden: boolean): Promise<void> {
  return call<void>(`/comments/${encodeURIComponent(commentId)}/hidden`, { method: 'PUT', ...json({ hidden }) });
}

export function reportComment(commentId: string, reason?: string): Promise<void> {
  return call<void>(`/comments/${encodeURIComponent(commentId)}/reports`, {
    method: 'POST',
    ...json(reason ? { reason } : {})
  });
}

export function listMyComments(): Promise<readonly MyComment[]> {
  return call<readonly MyComment[]>('/me/comments');
}
